/**
 * Browser geolocation — same options as salon skin-analysis AQI capture.
 */

/** Exact options salon kiosk uses for AQI / environment GPS. */
export const SALON_DEVICE_GEO_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};
export function geoErrorMessage(err) {
  const code = err?.code;
  if (code === 1) return "Location permission denied. Allow location access and try again.";
  if (code === 2) return "Location unavailable. Check GPS / network and try again.";
  if (code === 3) return "Location request timed out. Try again outdoors or near a window.";
  return err?.message || "Unable to get your location";
}

/**
 * Best-effort permission state: granted | prompt | denied | unsupported | unknown
 */
export async function queryDeviceLocationPermission() {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return "unsupported";
  }
  if (!navigator.permissions?.query) {
    return "unknown";
  }
  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result?.state || "unknown";
  } catch {
    return "unknown";
  }
}

function toSample(position) {
  return {
    latitude: Number(position.coords.latitude),
    longitude: Number(position.coords.longitude),
    accuracy: Number(position.coords.accuracy),
    timestamp: position.timestamp,
  };
}

export function getCurrentDevicePosition(options = {}) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.reject(new Error("Geolocation is not supported on this device"));
  }

  const {
    enableHighAccuracy = SALON_DEVICE_GEO_OPTIONS.enableHighAccuracy,
    timeout = SALON_DEVICE_GEO_OPTIONS.timeout,
    maximumAge = SALON_DEVICE_GEO_OPTIONS.maximumAge,
  } = options;

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve(toSample(position));
      },
      (err) => reject(new Error(geoErrorMessage(err))),
      { enableHighAccuracy, timeout, maximumAge }
    );
  });
}

/**
 * Watch until GPS accuracy improves. The first getCurrentPosition fix is often
 * a city-level network/IP point — do not use that alone for store pins.
 */
export function watchBestDevicePosition(options = {}, onUpdate) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.reject(new Error("Geolocation is not supported on this device"));
  }

  const {
    enableHighAccuracy = true,
    timeout = 22000,
    desiredAccuracyM = 45,
    minWaitMs = 2200,
  } = options;

  return new Promise((resolve, reject) => {
    let best = null;
    let settled = false;
    let watchId = null;
    const started = Date.now();

    const finish = (error) => {
      if (settled) return;
      settled = true;
      if (watchId != null) {
        try {
          navigator.geolocation.clearWatch(watchId);
        } catch {
          /* ignore */
        }
      }
      window.clearTimeout(timer);
      if (
        best &&
        Number.isFinite(best.latitude) &&
        Number.isFinite(best.longitude)
      ) {
        resolve(best);
        return;
      }
      reject(error || new Error("Unable to get your location"));
    };

    const consider = (position) => {
      const sample = toSample(position);
      if (!Number.isFinite(sample.latitude) || !Number.isFinite(sample.longitude)) return;
      if (!Number.isFinite(sample.accuracy) || sample.accuracy <= 0) {
        sample.accuracy = 99999;
      }
      if (!best || sample.accuracy < best.accuracy) {
        best = sample;
        try {
          onUpdate?.(sample);
        } catch {
          /* ignore */
        }
      }
      const elapsed = Date.now() - started;
      if (best.accuracy <= desiredAccuracyM && elapsed >= minWaitMs) {
        finish();
      }
    };

    try {
      navigator.geolocation.getCurrentPosition(consider, () => {}, {
        enableHighAccuracy,
        timeout: Math.min(8000, timeout),
        maximumAge: 0,
      });
    } catch {
      /* watchPosition still runs */
    }

    watchId = navigator.geolocation.watchPosition(
      consider,
      (err) => {
        if (best) {
          finish();
          return;
        }
        finish(new Error(geoErrorMessage(err)));
      },
      { enableHighAccuracy, timeout, maximumAge: 0 }
    );

    const timer = window.setTimeout(() => {
      if (best) {
        finish();
        return;
      }
      finish(
        new Error("Location request timed out. Try again outdoors or near a window.")
      );
    }, timeout);
  });
}

export const STORE_GPS_COARSE_M = 180;

/**
 * Same capture path as salon AQI: high-accuracy getCurrentPosition, then a
 * non-GPS fallback so Wi‑Fi / kiosk browsers still return a fix.
 */
export async function captureSalonDevicePosition(onUpdate) {
  try {
    const pos = await getCurrentDevicePosition(SALON_DEVICE_GEO_OPTIONS);
    try {
      onUpdate?.(pos);
    } catch {
      /* ignore */
    }
    return pos;
  } catch (firstErr) {
    try {
      const fallback = await getCurrentDevicePosition({
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 0,
      });
      try {
        onUpdate?.(fallback);
      } catch {
        /* ignore */
      }
      return fallback;
    } catch {
      throw firstErr;
    }
  }
}

/**
 * Store pin recapture: watch for a fresh high-accuracy fix (not a cached
 * network/IP point), then fall back to salon getCurrentPosition.
 */
export async function captureStoreDevicePosition(onUpdate) {
  try {
    return await watchBestDevicePosition(
      {
        enableHighAccuracy: true,
        timeout: 22000,
        desiredAccuracyM: 50,
        minWaitMs: 1200,
      },
      onUpdate
    );
  } catch (watchErr) {
    try {
      return await captureSalonDevicePosition(onUpdate);
    } catch {
      throw watchErr;
    }
  }
}
